import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  alpha,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import {
  AlertTriangle,
  Bot,
  MessageSquare,
  UserRound,
  Workflow as WorkflowIcon,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  MailAutomationAPI,
  type ScrapedEmailDetail,
} from '$api/MailAutomationAPI';
import {
  getWorkflowRun,
  type WorkflowRunDetail,
  type WorkflowRunMessage,
} from '$api/WorkflowRunsAPI';
import { AdminShell } from '$layouts/v2/AdminShell';
import { Stack } from '$layouts/v2/components/Stack';
import { WorkflowCanvas } from '$layouts/v2/components/WorkflowCanvas';
import { formatRelativeTime } from '$pages/mail-automation/utils';
import { useAccessToken } from '$src/authConfig';

import {
  readWorkflowRunEmailContext,
  WorkflowRunEmailContext,
} from './WorkflowRunEmailContextDrawer';
import { WorkflowRunStatusBadge } from './WorkflowRunStatusBadge';

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

type FailureConfigItem = { label: string; value: unknown };

const formatDiagnosticValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return 'Not set';
  }
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatStepKind = (kind: string): string =>
  kind
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (character) => character.toUpperCase());

const formatFieldList = (fields: v4.FieldSpec[]): string =>
  fields
    .map((field) => `${field.label || field.name} (${field.name})`)
    .join(', ');

function getFailureConfig(step: v4.StepSpec): FailureConfigItem[] {
  switch (step.kind) {
    case 'auto':
    case 'apiCall':
      return [
        { label: 'Service', value: step.serviceId },
        { label: 'Activity', value: step.activity },
        { label: 'Next step', value: step.next },
      ];
    case 'outlookCategory':
      return [
        { label: 'Activity', value: step.activity },
        { label: 'Category operation', value: step.operation },
        { label: 'Category', value: step.category },
        { label: 'Appearance', value: step.appearance },
        { label: 'Name format', value: step.nameFormat },
        { label: 'Next step', value: step.next },
      ];
    case 'userInput':
      return [
        { label: 'Prompt', value: step.prompt },
        { label: 'Fields', value: formatFieldList(step.fields) },
        { label: 'Next step', value: step.next },
      ];
    case 'llm':
      return [
        { label: 'Model', value: step.model },
        { label: 'Prompt', value: step.prompt },
        { label: 'Input fields', value: step.inputFields?.join(', ') },
        { label: 'Web search', value: step.webSearch ? 'Enabled' : undefined },
        { label: 'Output fields', value: formatFieldList(step.output) },
        { label: 'Next step', value: step.next },
      ];
    case 'systemOne':
      return [
        { label: 'Model', value: step.model },
        { label: 'State fields', value: step.stateFields?.join(', ') },
        { label: 'Questions', value: Object.keys(step.questions).join(', ') },
        { label: 'Next step', value: step.next },
      ];
    case 'branch':
      return [
        { label: 'Selected field', value: step.selectField },
        { label: 'Routes', value: step.cases },
        { label: 'Default route', value: step.default },
      ];
    case 'decision':
      return [
        { label: 'Question', value: step.prompt },
        {
          label: 'Routes',
          value: step.actions.map(
            (action) => `${action.label} → ${action.next}`,
          ),
        },
      ];
    case 'subWorkflow':
      return [
        { label: 'Referenced workflow', value: step.workflowId },
        { label: 'Input mapping', value: step.inputs },
        { label: 'Output mapping', value: step.outputs },
        { label: 'After completion', value: step.onDone },
      ];
    case 'final':
      return [{ label: 'Message', value: step.message }];
  }
}

function FailureDiagnostics({
  detail,
  failedStepId,
}: {
  detail: WorkflowRunDetail;
  failedStepId: string | null;
}) {
  const failedStep = failedStepId
    ? detail.workflowVersion?.spec.steps[failedStepId]
    : undefined;
  const config = failedStep ? getFailureConfig(failedStep) : [];
  const dataKeys = Object.keys(detail.run.data);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: (theme) => alpha(theme.palette.error.main, 0.55),
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.035),
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AlertTriangle size={18} color="var(--mui-palette-error-main)" />
          <Typography variant="subtitle2">Why this workflow failed</Typography>
        </Stack>

        <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>
          {detail.run.error ||
            'The run failed, but no technical error message was recorded.'}
        </Alert>

        {failedStep && failedStepId ? (
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Failed step
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 650 }}>
              {failedStep.label} · {formatStepKind(failedStep.kind)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Step ID: {failedStepId}
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            The failed step could not be identified from this run record.
          </Typography>
        )}

        {config.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Configuration of the failed step
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {config.map((item) => (
                <Box key={item.label}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                  >
                    {formatDiagnosticValue(item.value)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {dataKeys.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Data available when it failed
            </Typography>
            <Typography variant="body2">{dataKeys.join(', ')}</Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

const rootActiveStepId = (activeState: unknown): string | null => {
  if (typeof activeState === 'string') {
    return activeState;
  }
  if (!activeState || typeof activeState !== 'object') {
    return null;
  }
  return Object.keys(activeState)[0] ?? null;
};

function MessageBubble({ message }: { message: WorkflowRunMessage }) {
  const isUser = message.author.type === 'user';
  const label = message.author.type === 'user' ? message.author.name : 'Bert';
  const AuthorIcon = isUser ? UserRound : Bot;

  return (
    <Stack
      spacing={0.4}
      alignItems={isUser ? 'flex-end' : 'flex-start'}
      sx={{ width: '100%' }}
    >
      <Stack
        direction={isUser ? 'row-reverse' : 'row'}
        spacing={0.5}
        alignItems="center"
        sx={{ px: 0.25 }}
      >
        <AuthorIcon size={11} />
        <Typography variant="caption">{label}</Typography>
        {message.pending && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CircularProgress size={9} thickness={5} />
            <Typography variant="caption">Working…</Typography>
          </Stack>
        )}
      </Stack>
      <Box
        sx={{
          maxWidth: '92%',
          px: 1.25,
          py: 0.9,
          border: 1,
          borderColor: (theme) =>
            isUser
              ? alpha(theme.palette.secondary.main, 0.35)
              : theme.palette.divider,
          borderRadius: isUser ? '10px 3px 10px 10px' : '3px 10px 10px 10px',
          bgcolor: (theme) =>
            isUser
              ? alpha(theme.palette.secondary.main, 0.12)
              : theme.palette.background.default,
          overflowWrap: 'anywhere',
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children }) => (
              <Typography
                component="p"
                variant="body2"
                sx={{ '&:not(:last-child)': { mb: 1 }, lineHeight: 1.5 }}
              >
                {children}
              </Typography>
            ),
            ul: ({ children }) => (
              <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
                {children}
              </Box>
            ),
            ol: ({ children }) => (
              <Box component="ol" sx={{ my: 0.5, pl: 2.5 }}>
                {children}
              </Box>
            ),
            li: ({ children }) => (
              <Box component="li" sx={{ pl: 0.25, lineHeight: 1.5 }}>
                {children}
              </Box>
            ),
            strong: ({ children }) => (
              <Box component="strong" sx={{ fontWeight: 700 }}>
                {children}
              </Box>
            ),
            em: ({ children }) => (
              <Box component="em" sx={{ fontStyle: 'italic' }}>
                {children}
              </Box>
            ),
          }}
        >
          {message.text}
        </ReactMarkdown>
      </Box>
    </Stack>
  );
}

export default function WorkflowRunsDetailPage() {
  const { workflowRunId = '' } = useParams<{ workflowRunId: string }>();
  const { token } = useAccessToken();
  const latestRequestIdRef = useRef(0);
  const messagesRef = useRef<HTMLDivElement>(null);
  const [detail, setDetail] = useState<WorkflowRunDetail | null>(null);
  const [email, setEmail] = useState<ScrapedEmailDetail | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [runView, setRunView] = useState<'chat' | 'workflow'>('chat');

  const load = useCallback(() => {
    if (!token || !workflowRunId) {
      return;
    }

    const requestId = ++latestRequestIdRef.current;
    setIsLoading(true);
    setIsEmailLoading(false);
    setError(null);
    setEmail(null);
    setEmailError(null);

    getWorkflowRun(token, workflowRunId)
      .then((response) => {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        setDetail(response);
        setIsLoading(false);
        setIsEmailLoading(true);

        MailAutomationAPI.getScrapedEmailById(token, response.run.emailId)
          .then((scrapedEmail) => {
            if (requestId === latestRequestIdRef.current) {
              setEmail(scrapedEmail);
            }
          })
          .catch((emailLoadError) => {
            if (requestId === latestRequestIdRef.current) {
              setEmailError(String(emailLoadError));
            }
          })
          .finally(() => {
            if (requestId === latestRequestIdRef.current) {
              setIsEmailLoading(false);
            }
          });
      })
      .catch((runLoadError) => {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        setError(String(runLoadError));
        setDetail(null);
        setIsLoading(false);
      });
  }, [token, workflowRunId]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (messagesRef.current) {
        messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [detail?.run.messages.length]);

  const activeStepId = useMemo(
    () => rootActiveStepId(detail?.run.activeState),
    [detail],
  );
  const failedStepId = useMemo(
    () =>
      detail?.run.failedStepId ??
      [...(detail?.run.messages ?? [])]
        .reverse()
        .find((message) => message.stepId)?.stepId ??
      null,
    [detail],
  );
  const emailContext = useMemo(
    () =>
      detail
        ? readWorkflowRunEmailContext(detail.run.data)
        : { current: null, previous: [] },
    [detail],
  );

  const title = detail?.run.subject || 'Workflow Run';
  const workflowTitle =
    detail?.workflowVersion?.title || detail?.run.workflowId || '';
  const activeStepLabel =
    activeStepId && detail?.workflowVersion
      ? detail.workflowVersion.spec.steps[activeStepId]?.label || activeStepId
      : null;
  return (
    <AdminShell title={title} backAction="/v2/mail-automation/workflow-runs">
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {isLoading && (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={66} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' },
              gap: 1.5,
            }}
          >
            <Skeleton variant="rounded" height={620} />
            <Skeleton variant="rounded" height={620} />
          </Box>
        </Stack>
      )}

      {!isLoading && detail && (
        <>
          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={{ px: 1.5, py: 1.1 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 1, md: 1.5 }}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ minWidth: 0, flex: '1 1 280px' }}
                >
                  <WorkflowRunStatusBadge
                    result={detail.run.result}
                    size="medium"
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: 650, lineHeight: 1.35 }}
                    >
                      {workflowTitle}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ display: 'block', lineHeight: 1.35 }}
                    >
                      {detail.run.workflowId} · v{detail.run.workflowVersion}
                      {activeStepLabel ? ` · ${activeStepLabel}` : ''}
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction="row"
                  spacing={{ xs: 1.5, sm: 2.25 }}
                  alignItems="center"
                  sx={{
                    flexShrink: 0,
                    justifyContent: { xs: 'space-between', md: 'flex-end' },
                  }}
                >
                  <Tooltip
                    title={`Started ${formatDateTime(detail.run.createdAt)}`}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', lineHeight: 1.25 }}
                      >
                        Started
                      </Typography>
                      <Typography variant="body2" noWrap>
                        {formatRelativeTime(detail.run.createdAt)}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip
                    title={`Updated ${formatDateTime(detail.run.updatedAt)}`}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', lineHeight: 1.25 }}
                      >
                        Updated
                      </Typography>
                      <Typography variant="body2" noWrap>
                        {formatRelativeTime(detail.run.updatedAt)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Stack>
              </Stack>
            </Paper>

            {detail.run.result === 'failed' && (
              <FailureDiagnostics detail={detail} failedStepId={failedStepId} />
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  lg: 'minmax(320px, 0.8fr) minmax(0, 1.45fr)',
                },
                gridTemplateRows: {
                  xs: 'minmax(520px, 68vh) minmax(520px, 68vh)',
                  lg: 'minmax(600px, 1fr)',
                },
                gap: 1.5,
                height: { lg: 'calc(100vh - 174px)' },
                minHeight: { lg: 600 },
              }}
            >
              <WorkflowRunEmailContext
                context={emailContext}
                email={email}
                isEmailLoading={isEmailLoading}
                emailError={emailError}
                fallbackSubject={detail.run.subject}
                fallbackMailbox={detail.run.mailbox?.mailAddress || ''}
                attachments={detail.run.attachments}
                token={token}
                workflowRunId={workflowRunId}
              />

              <Paper
                variant="outlined"
                sx={{
                  minWidth: 0,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  sx={{ minHeight: 46, px: 1.5, flexShrink: 0 }}
                >
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                      Run activity
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', lineHeight: 1.25 }}
                    >
                      Chat and workflow execution
                    </Typography>
                  </Box>
                  <Tabs
                    value={runView}
                    onChange={(_event, value: 'chat' | 'workflow') =>
                      setRunView(value)
                    }
                    sx={{
                      minHeight: 36,
                      alignSelf: { xs: 'stretch', sm: 'center' },
                      '& .MuiTab-root': {
                        minHeight: 36,
                        minWidth: { xs: 0, sm: 88 },
                        px: 1,
                        py: 0.5,
                        fontSize: 12,
                      },
                    }}
                  >
                    <Tab
                      value="chat"
                      icon={<MessageSquare size={14} />}
                      iconPosition="start"
                      label={`Chat (${detail.run.messages.length})`}
                    />
                    <Tab
                      value="workflow"
                      icon={<WorkflowIcon size={14} />}
                      iconPosition="start"
                      label="Workflow"
                    />
                  </Tabs>
                </Stack>
                <Divider />
                {runView === 'chat' ? (
                  <Stack
                    ref={messagesRef}
                    spacing={1.5}
                    sx={{
                      flexGrow: 1,
                      minHeight: 0,
                      p: 1.5,
                      overflowY: 'auto',
                      bgcolor: (theme) =>
                        alpha(theme.palette.background.default, 0.36),
                    }}
                  >
                    {detail.run.messages.length === 0 ? (
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        spacing={1}
                        sx={{
                          minHeight: 180,
                          height: '100%',
                          color: 'text.secondary',
                          textAlign: 'center',
                        }}
                      >
                        <MessageSquare size={22} />
                        <Typography variant="body2">
                          No chat messages were recorded for this run.
                        </Typography>
                      </Stack>
                    ) : (
                      detail.run.messages.map((message, index) => (
                        <MessageBubble
                          key={message.id || `${message.author.type}-${index}`}
                          message={message}
                        />
                      ))
                    )}
                  </Stack>
                ) : (
                  <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
                    {detail.workflowVersion ? (
                      <WorkflowCanvas
                        spec={detail.workflowVersion.spec}
                        title={detail.workflowVersion.title}
                        workflowId={detail.workflowVersion.workflowId}
                        trigger="email"
                        mode="view"
                        activeStepId={activeStepId}
                      />
                    ) : (
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        spacing={1}
                        sx={{ height: '100%', p: 3, textAlign: 'center' }}
                      >
                        <WorkflowIcon size={24} />
                        <Typography
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          The pinned workflow version for this run could not be
                          found.
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                )}
              </Paper>
            </Box>
          </Stack>
        </>
      )}

      {!isLoading && !detail && (
        <Alert severity="warning">Workflow run not found.</Alert>
      )}
    </AdminShell>
  );
}
